<template>
  <div>
    <!-- Need to set a unique key so that header isn't shared-->
    <VModal
      v-if="isSuccessful"
      id="aaq-modal"
      :key="Date.now()"
      :showing="isSuccessful"
      hide-close="hideClose"
      data-testid="ask-a-question-success"
    >
      <div class="max-w-full text-center w-96">
        <div class="flex items-center justify-center w-12 h-12 m-auto mb-5 bg-green-100 rounded-full">
          <CheckIcon class="w-6 text-copper-700" />
        </div>
        <p class="mb-6 text-lg font-medium">Your question has been sent.</p>
        <VButton
          class="w-full"
          type="button"
          data-testid="ask-a-question-close-success"
          variant="copper"
          size="lg"
          @click="handleSuccessClose"
          >Done</VButton
        >
      </div>
    </VModal>
    <VModal
      v-else
      id="aaq-modal"
      class="text-gray-900"
      data-testid="ask-a-question-modal"
      :showing="showing"
      @update:showing="$emit('update:showing', $event)"
    >
      <template #header>
        <p class="pr-16 mb-0 text-lg">What would you like to ask {{ company.name }}?</p>
        <p class="mb-0 text-sm text-gray-600">Replies will be sent to your email below</p>
      </template>
      <template #default>
        <ModalsAskAQuestionForm :user="user" @on-submit="onSubmit" />
      </template>
    </VModal>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'ramda'
import { CheckIcon } from '@heroicons/vue/24/solid'
import type { DeepReadonly, PropType, Ref } from 'vue'
import type { Company } from '@/entities/company'
import type { Deck, IncludeCompany, Slide } from '@/entities/deck'
import { LOCATION_DICTIONARY } from '@/entities/deck'

interface QuestionPayload {
  name: string
  email: string
  question: string
}

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    default: undefined,
    required: false,
  },
  analyticsLocation: {
    type: String as PropType<'deck' | 'companyPage'>,
    required: true,
  },
  company: {
    type: Object as PropType<Company>,
    required: true,
  },
  showing: {
    type: Boolean,
    required: true,
  },
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    default: () => ({}),
  },
})

const emit = defineEmits(['update:showing'])

const { $analytics, $sentry } = useNuxtApp()
const { state: userState, setGuestUserInfo } = useUser()
const { hideAlert, showAlert } = useAlerts()
const isSuccessful: Ref<boolean> = ref(false)
const user = computed(() => userState.value.user || userState.value.guestUserInfo || undefined)

function handleSuccessClose() {
  emit('update:showing', false)
  isSuccessful.value = false
}

async function onSubmit(formData: QuestionPayload) {
  hideAlert()

  try {
    await _submitQuestion(formData)
    _updateGuestUserEmail(formData)
    _trackQuestionSubmission()

    isSuccessful.value = true
  } catch (e) {
    $sentry?.captureException(e)

    showAlert({
      alertType: 'danger',
      alertMessage: "Sorry, we couldn't submit your question. Please try again later.",
      alertTimeout: 3000,
    })

    emit('update:showing', false)
    _trackQuestionSubmissionError(e)
  }
}

async function _submitQuestion({ name, email, question }: QuestionPayload) {
  const data = isEmpty(props.slide) ? { name, question, email_address: email } : _formatQuestionPayload({ name, email, question })
  await $fetch(`v2/companies/${props.company.id}/questions`, {
    method: 'POST',
    body: { company_id: props.company.id, ...data },
    ...useFetchSharedOptions(),
  })
}

function _formatQuestionPayload({ name, email, question }: QuestionPayload) {
  const [locationType, locationId] = _getLocationTypeAndId(props.slide)

  return {
    question,
    deck_id: props.slide.deck_id,
    slide_id: props.slide.id,
    location_type: locationType,
    location_id: locationId,
    email_address: email,
    name,
  }
}

function _getLocationTypeAndId(slide: DeepReadonly<Slide>): [string | undefined, number | null | undefined] {
  switch (slide.type) {
    case 'P':
    case 'I':
      return [LOCATION_DICTIONARY[slide.type], slide.file?.id]
    case 'M':
      return [LOCATION_DICTIONARY[slide.type], slide.model_id]
    case 'S':
      return [LOCATION_DICTIONARY[slide.type], slide.site_id]
    default:
      return [undefined, undefined]
  }
}

function _updateGuestUserEmail({ email }: { email: string }) {
  if (userState.value.user) return
  setGuestUserInfo({ ...userState.value.guestUserInfo, email: email.trim() })
}

function _trackQuestionSubmission() {
  $analytics.track('AAQClickSubmitQuestion', {
    deckId: props.slide?.deck_id,
    deckName: props.deck?.title,
    companyId: props.company.id,
    companyName: props.company.name,
    location: props.analyticsLocation,
  })
}

function _trackQuestionSubmissionError(error: any) {
  $analytics.track('askAQuestionButtonSubmittedError', {
    deckId: props.slide?.deck_id,
    error: error?.response?.status,
  })
}

onMounted(() => {
  if (props.showing) {
    $analytics.track('showedScreenAskAQuestion')
  }
})
</script>
